import React, { useRef, useEffect, useContext } from 'react';
import { ThemeContext } from '../context/ThemeContext';

function CursorTrail() {
  const canvasRef = useRef(null);
  const { isDarkMode } = useContext(ThemeContext);
  const isDarkRef = useRef(isDarkMode);
  const mouseRef = useRef({ x: null, y: null });
  const trailRef = useRef([]);
  const animFrameRef = useRef(null);
  const isTouchDevice =
    typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  useEffect(() => {
    isDarkRef.current = isDarkMode;
  }, [isDarkMode]);

  useEffect(() => {
    if (isTouchDevice) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    // Hide default cursor
    document.documentElement.style.cursor = 'none';

    function resize() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }
    resize();

    function onMouseMove(e) {
      mouseRef.current = { x: e.clientX, y: e.clientY };
      // Add trail point
      trailRef.current.push({
        x: e.clientX,
        y: e.clientY,
        opacity: 1.0,
        radius: 5,
      });
      // Cap trail length
      if (trailRef.current.length > 50) {
        trailRef.current.shift();
      }
    }

    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const dark = isDarkRef.current;
      const trail = trailRef.current;
      const mouse = mouseRef.current;

      // Draw and decay trail points
      for (let i = trail.length - 1; i >= 0; i--) {
        const point = trail[i];
        point.opacity -= 0.025;

        if (point.opacity <= 0) {
          trail.splice(i, 1);
          continue;
        }

        const r = point.radius * point.opacity;
        if (r <= 0) continue;

        const gradient = ctx.createRadialGradient(point.x, point.y, 0, point.x, point.y, r * 2);

        if (dark) {
          gradient.addColorStop(0, `rgba(100, 200, 255, ${point.opacity * 0.8})`);
          gradient.addColorStop(0.4, `rgba(130, 170, 255, ${point.opacity * 0.4})`);
          gradient.addColorStop(1, 'rgba(100, 200, 255, 0)');
        } else {
          gradient.addColorStop(0, `rgba(102, 126, 234, ${point.opacity * 0.8})`);
          gradient.addColorStop(0.4, `rgba(118, 75, 162, ${point.opacity * 0.4})`);
          gradient.addColorStop(1, 'rgba(102, 126, 234, 0)');
        }

        ctx.beginPath();
        ctx.arc(point.x, point.y, r * 2, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();
      }

      // Draw main cursor dot
      if (mouse.x !== null) {
        const cursorRadius = 8;
        const cursorGradient = ctx.createRadialGradient(
          mouse.x,
          mouse.y,
          0,
          mouse.x,
          mouse.y,
          cursorRadius * 2
        );

        if (dark) {
          cursorGradient.addColorStop(0, 'rgba(180, 220, 255, 0.9)');
          cursorGradient.addColorStop(0.3, 'rgba(100, 180, 255, 0.6)');
          cursorGradient.addColorStop(1, 'rgba(100, 180, 255, 0)');
        } else {
          cursorGradient.addColorStop(0, 'rgba(102, 126, 234, 0.9)');
          cursorGradient.addColorStop(0.3, 'rgba(118, 75, 162, 0.6)');
          cursorGradient.addColorStop(1, 'rgba(102, 126, 234, 0)');
        }

        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, cursorRadius * 2, 0, Math.PI * 2);
        ctx.fillStyle = cursorGradient;
        ctx.fill();

        // Inner bright dot
        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, 3, 0, Math.PI * 2);
        ctx.fillStyle = dark ? 'rgba(220, 240, 255, 0.95)' : 'rgba(102, 126, 234, 0.95)';
        ctx.fill();
      }

      animFrameRef.current = requestAnimationFrame(animate);
    }

    animate();

    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', onMouseMove);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouseMove);
      document.documentElement.style.cursor = '';
    };
  }, [isTouchDevice]);

  if (isTouchDevice) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 9999,
        pointerEvents: 'none',
      }}
    />
  );
}

export default CursorTrail;
